/* Zenthesia glossary: term count, A to Z state, search, topic filter, sticky index bar.
   Search matches term names and abbreviations first; when no name matches, it falls back to terms that
   mention the query. init() re-reads the DOM, so it is safe to run again after the theme editor re-renders a section. */
(() => {
  const norm = (t) => String(t).toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '').replace(/[-\s]+/g, ' ').trim();
  let state = null;
  let ticking = false;

  function updateStuck() {
    ticking = false;
    if (!state || !state.bar.isConnected) return;
    const { bar, sentinel, sections } = state;
    const visible = sections.filter((s) => s.isConnected && !s.hidden);
    const last = visible[visible.length - 1];
    const stuck = Boolean(last) && sentinel.getBoundingClientRect().top < 0 && last.getBoundingClientRect().bottom > bar.offsetHeight;
    bar.classList.toggle('is-stuck', stuck);
    sentinel.style.height = stuck ? `${bar.offsetHeight}px` : '0px';
    document.documentElement.style.setProperty('--zg-bar-h', `${bar.offsetHeight}px`);
  }

  function init() {
    const bar = document.querySelector('[data-zg-bar]');
    const sentinel = document.querySelector('[data-zg-bar-sentinel]');
    const sections = [...document.querySelectorAll('[data-zg-terms]')];
    const terms = [...document.querySelectorAll('.zg-term')];
    if (!bar || !sentinel || !terms.length) {
      state = null;
      return;
    }
    state = { bar, sentinel, sections };

    document.querySelectorAll('.zen-research a[href^="http"]').forEach((a) => {
      if (a.host && a.host !== location.host && !a.target) {
        a.target = '_blank';
        a.rel = 'noopener';
      }
    });

    const total = document.querySelector('[data-zg-fact="total"]');
    if (total) {
      total.innerHTML = `<b>${terms.length}</b> ${terms.length === 1 ? 'term' : 'terms'}`;
      total.hidden = false;
    }

    const items = terms.map((el) => ({
      el,
      topic: el.dataset.topic,
      name: norm(`${el.dataset.name} ${el.dataset.short || ''}`),
      text: norm(el.textContent),
    }));
    const groups = [...document.querySelectorAll('[data-zg-letter]')];
    const letters = [...bar.querySelectorAll('[data-zg-jump]')];
    const controls = bar.querySelector('[data-zg-controls]');
    const search = bar.querySelector('[data-zg-search]');
    const radios = [...bar.querySelectorAll('input[name="zg-topic"]')];
    const select = bar.querySelector('[data-zg-topic-select]');
    const count = bar.querySelector('[data-zg-count]');
    const note = bar.querySelector('[data-zg-note]');
    const empty = bar.querySelector('[data-zg-empty]');
    const clear = bar.querySelector('[data-zg-clear]');
    if (!controls || !search || !select) return;

    const topic = () => (radios.find((r) => r.checked) || { value: 'all' }).value;
    const setTopic = (value) => {
      radios.forEach((r) => { r.checked = r.value === value; });
      select.value = value;
    };

    function apply(scroll) {
      const query = norm(search.value);
      const t = topic();
      const pool = t === 'all' ? items : items.filter((i) => i.topic === t);
      let matches = query ? pool.filter((i) => i.name.includes(query)) : pool;
      const fallback = Boolean(query) && !matches.length;
      if (fallback) matches = pool.filter((i) => i.text.includes(query));
      const shown = new Set(matches.map((i) => i.el));
      items.forEach((i) => { i.el.hidden = !shown.has(i.el); });
      groups.forEach((g) => { g.hidden = !g.querySelector('.zg-term:not([hidden])'); });
      sections.forEach((s) => { s.hidden = !s.querySelector('[data-zg-letter]:not([hidden])'); });
      letters.forEach((a) => {
        const g = document.getElementById(`letter-${a.dataset.zgJump}`);
        const live = Boolean(g) && !g.hidden;
        a.classList.toggle('is-empty', !live);
        if (live) {
          a.href = `#letter-${a.dataset.zgJump}`;
          a.removeAttribute('aria-disabled');
          a.removeAttribute('tabindex');
        } else {
          a.removeAttribute('href');
          a.setAttribute('aria-disabled', 'true');
          a.tabIndex = -1;
        }
      });
      const filtered = Boolean(query) || t !== 'all';
      count.textContent = filtered ? `Showing ${shown.size} of ${items.length}` : `${items.length} terms`;
      note.hidden = !(fallback && shown.size);
      if (!note.hidden) note.textContent = `No term names match “${search.value.trim()}”. Showing terms that mention it.`;
      empty.hidden = shown.size !== 0;
      clear.textContent = query ? 'Clear search' : 'Show all topics';
      if (scroll && bar.classList.contains('is-stuck')) sentinel.scrollIntoView({ block: 'start' });
      updateStuck();
    }

    function reset() {
      search.value = '';
      setTopic('all');
      apply(false);
    }

    // Reveal a term that a link, the hash or the theme editor points at, even if a filter hides it.
    function reveal(el) {
      if (el && el.closest('.zg-term[hidden], [data-zg-letter][hidden], [data-zg-terms][hidden]')) {
        reset();
        el.scrollIntoView({ block: 'start' });
      }
    }

    search.oninput = () => apply(true);
    radios.forEach((r) => { r.onchange = () => { select.value = r.value; apply(true); }; });
    select.onchange = () => { setTopic(select.value); apply(true); };
    clear.onclick = () => { reset(); search.focus(); };
    state.reveal = (id) => reveal(id && document.getElementById(id));
    controls.hidden = false;
    apply(false);
  }

  window.addEventListener('scroll', () => {
    if (!ticking) {
      ticking = true;
      requestAnimationFrame(updateStuck);
    }
  }, { passive: true });
  window.addEventListener('resize', updateStuck);
  window.addEventListener('hashchange', () => state?.reveal?.(decodeURIComponent(location.hash.slice(1))));
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:reorder', init);
  document.addEventListener('shopify:section:unload', () => setTimeout(init, 0));
  document.addEventListener('shopify:block:select', (e) => state?.reveal?.(e.target.id));
  init();
})();
